import * as React from 'react';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { Table, Popup } from 'semantic-ui-react';
import { Field } from 'react-final-form';

// Global Types
import { AppState } from 'services/types';

// Actions
import {
    setSelectedTableCell,
    addSelectedTableRow,
    removeSelectedTableRow
    // @ts-ignore
} from 'services/selectedTableCells/actions';

// Selectors
import {
    makeSelectIsCellSelected,
    makeSelectIsCellActive
    // @ts-ignore
} from 'services/selectedTableCells/selectors';
import {
    makeSelectValuesTimesheetEntryValue,
    makeSelectErrorsTimesheetEntryValue,
    makeSelectUserHasPermissionToEdit
    // @ts-ignore
} from 'services/timesheetTimesheetEntriesForm/selectors';

// Components
// @ts-ignore
import FormTimesheetEntryValue from '../FormTimesheetEntryValue';

interface OwnProps {
    children?: any;
    rowId: number;
    cellName: string;
    component: any;
    isPieceRateField?: boolean;
    entityTypeId?: number;
}

interface ConnectedProps {
    workTypeCompositeKey?: string;
    isApproved?: boolean;
    error?: string;
    isCellSelected: boolean;
    isCellActive: boolean;
    isPieceRateField?: boolean;
    userHasPermissionToEdit?: boolean;
}

interface DispatchProps {
    setSelectedTableCell: Function;
    addSelectedTableRow: Function;
    removeSelectedTableRow: Function;
}

type Props = OwnProps & ConnectedProps & DispatchProps;

export class SelectableTableCell extends React.PureComponent<Props> {
    componentDidUpdate(prevProps: Props): void {
        const {
            rowId,
            cellName,
            isApproved,
            isCellSelected,
            ...props
        } = this.props;
        // Deselect cell when entry is approved
        if (!prevProps.isApproved && isApproved && isCellSelected) {
            props.removeSelectedTableRow(rowId);
        }
    }

    componentWillUnmount(): void {
        const { rowId, ...props } = this.props;
        props.removeSelectedTableRow(rowId);
    }

    get isDisabled(): boolean {
        const {
            isApproved,
            isPieceRateField,
            workTypeCompositeKey,
            userHasPermissionToEdit
        } = this.props;

        if (!userHasPermissionToEdit) return true;

        const [, isPieceRateString] = workTypeCompositeKey
            ? workTypeCompositeKey.split('.')
            : [];
        const isPieceRate = isPieceRateString === 'true';

        if (!isApproved && isPieceRateField) {
            return !isPieceRate;
        }
        return !!isApproved;
    }

    handleClick = (event: any): void => {
        const { rowId, cellName, isCellSelected } = this.props;

        // TODO
        // Shift-click is "and everthing inbetween" operation

        // TODO
        // Ctrl-click is an "and" operation

        if (event.shiftKey) {
            if (isCellSelected) {
                this.props.removeSelectedTableRow(rowId);
            } else {
                this.props.addSelectedTableRow(rowId, cellName);
            }
        } else {
            this.props.setSelectedTableCell(rowId, cellName);
        }
    };

    render(): React.ReactElement {
        const {
            children,
            rowId,
            cellName,
            isApproved,
            error,
            isCellSelected,
            isCellActive,
            ...props
        } = this.props;

        // Disabled, Error, Selected

        const Content = (
            <Table.Cell
                active={this.isDisabled}
                onClick={!this.isDisabled && this.handleClick}
                style={{
                    cursor: this.isDisabled ? 'auto' : 'pointer',
                    position: 'relative'
                }}
            >
                {isCellSelected && !this.isDisabled && (
                    <div
                        style={{
                            position: 'absolute',
                            border: isCellActive
                                ? '2px solid #2F497A'
                                : undefined,
                            backgroundColor: '#BADDF5',
                            borderRadius: 1,
                            top: 0,
                            right: 0,
                            bottom: 0,
                            left: 0
                        }}
                    />
                )}
                {error && (
                    <div
                        style={{
                            position: 'absolute',
                            border: isCellActive
                                ? '2px solid #E3142E'
                                : undefined,
                            backgroundColor: '#F8C4CA',
                            borderRadius: 1,
                            top: 0,
                            right: 0,
                            bottom: 0,
                            left: 0
                        }}
                    />
                )}
                <div
                    style={{
                        height: isCellActive ? 0 : 'auto',
                        overflow: isCellActive ? 'hidden' : 'visible',
                        position: 'relative',
                        paddingRight: 1
                    }}
                >
                    <FormTimesheetEntryValue rowId={rowId} cellName={cellName}>
                        {children}
                    </FormTimesheetEntryValue>
                </div>
                {isCellActive && (
                    <Field
                        name={`${rowId}.${cellName}`}
                        forceFocus={isCellActive}
                        rowId={rowId}
                        {...props}
                    />
                )}
            </Table.Cell>
        );

        return (
            <Popup
                content={error}
                style={!error ? { opacity: 0 } : {}}
                trigger={Content}
            />
        );
    }
}

const mapStateToProps = createStructuredSelector<
    AppState,
    OwnProps,
    ConnectedProps
>({
    workTypeCompositeKey: makeSelectValuesTimesheetEntryValue(
        'workTypeCompositeKey'
    ),
    isApproved: makeSelectValuesTimesheetEntryValue('lastReviewIsApproved'),
    error: makeSelectErrorsTimesheetEntryValue(),
    isCellSelected: makeSelectIsCellSelected(),
    isCellActive: makeSelectIsCellActive(),
    userHasPermissionToEdit: makeSelectUserHasPermissionToEdit()
});

export default connect(
    mapStateToProps,
    { setSelectedTableCell, addSelectedTableRow, removeSelectedTableRow }
)(SelectableTableCell);
